import axios from "axios";
import config from "../config";

export interface AddressPod {
    id: number;
    addressTraceId: string;
    podUrl: string;
    trackingNumber: string | null;
    latitude: number | null;
    longitude: number | null;
    createdAt: string;
}

export interface Address {
    id: number;
    street1: string;
    street2?: string | null;
    city: string;
    stateId?: number | null;
    stateCode?: string | null;
    zip: string;
    zipSuffix?: string | null;
    country: string;
    latitude?: number | null;
    longitude?: number | null;
    googleLatitude?: number | null;
    googleLongitude?: number | null;
    radarLatitude?: number | null;
    radarLongitude?: number | null;
    uberLatitude?: number | null;
    uberLongitude?: number | null;
    traceId?: string | null;
    isValid?: boolean | null;
    isGoogleValid?: boolean | null;
    googleScore?: number | null;
    googlePlaceId?: string | null;
    deliveryTime?: number;
    googleFormattedAddress?: string | null;
    googlePossibleNextAction?: string | null;
    isRadarValid?: boolean | null;
    radarScore?: number | null;
    radarPlaceId?: string | null;
    radarFormattedAddress?: string | null;
    radarGeoconfidence?: string | null;
    radarVerificationStatus?: string | null;
    isUberValid?: boolean | null;
    uberPlaceId?: string | null;
    validationFeedback?: string | null;
    validationSource?: string | null;
    geoFenceConfidence?: string | null;
    coordinateSource?: string | null;
    destinationType?: string | null;
    addressType?: string | null;
    status?: string | null;
    isApartment?: boolean | null;
    supersededByAddressId?: number | null;
    supersededByTraceId?: string | null;
    supersededByFullAddress?: string | null;
    createdAt: string;
    updatedAt?: string | null;
    addressPods?: AddressPod[] | null;
    hasGeminiPreprocessing?: boolean;
}

export const getLatestDeliveredCoordinates = (address: Address): { lat: number; lng: number } | null => {
    const podsWithCoordinates = (address.addressPods || [])
        .filter((pod): pod is AddressPod & { latitude: number; longitude: number } => pod.latitude != null && pod.longitude != null)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const latestPod = podsWithCoordinates[0];
    return latestPod ? { lat: latestPod.latitude, lng: latestPod.longitude } : null;
};

export interface DeliveredCoordinateMarker {
    id: number;
    lat: number;
    lng: number;
    podUrl: string;
    trackingNumber?: string | null;
    createdAt: string;
}

export const getAllDeliveredCoordinates = (address: Address): DeliveredCoordinateMarker[] => {
    return (address.addressPods || [])
        .filter((pod): pod is AddressPod & { latitude: number; longitude: number } => pod.latitude != null && pod.longitude != null)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((pod) => ({
            id: pod.id,
            lat: pod.latitude,
            lng: pod.longitude,
            podUrl: pod.podUrl,
            trackingNumber: pod.trackingNumber,
            createdAt: pod.createdAt
        }));
};

export interface AddressQueryParams {
    googlePossibleNextAction?: string;
    geoFenceConfidence?: string;
    isGoogleValid?: boolean;
    isRadarValid?: boolean;
    radarGeoconfidence?: string;
    radarVerificationStatus?: string;
    dateFrom?: string;
    dateTo?: string;
    offset?: number;
    limit?: number;
    orderBy?: string;
    orderByDescending?: boolean;
    traceIds?: string;
    isApartment?: boolean;
    destinationType?: string;
    hasPod?: boolean;
    fullAddress?: string;
    hasGeminiPreprocessing?: boolean;
    geminiStatus?: string;
    geminiCorrectionAccepted?: boolean;
    coordinateSource?: string;
}

export interface AddressResponse {
    items: Address[];
    page: number;
    pageSize: number;
    totalItems: number;
}

export interface AddressNote {
    id: number;
    addressId: number;
    addressTraceId: string;
    note: string;
    noteType: string;
    userEmail: string | null;
    userFullName: string | null;
    source: string | null;
    createdAt: string;
    updatedAt: string | null;
}

export interface CreateAddressNoteRequest {
    note: string;
    noteType?: string;
    userEmail?: string;
    userFullName?: string;
    source?: string;
}

export interface AddressHistoryRecord {
    id: number;
    addressId: number;
    addressTraceId: string;
    previousAddressSnapshot: string;
    newAddressSnapshot: string;
    changeSummary: string | null;
    changeSource: string;
    changeReason: string | null;
    changedBy: string | null;
    metadata: string | null;
    createdAt: string;
    updatedAt: string | null;
}

export interface ParsedAddress {
    id?: number;
    street1?: string;
    street2?: string;
    city?: string;
    stateCode?: string;
    zip?: string;
    zipSuffix?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    isGoogleValid?: boolean;
    googleScore?: number;
    isRadarValid?: boolean;
    radarScore?: number;
    validationFeedback?: string;
}

export interface AddressValidationInfo {
    id: number;
    addressId: number;
    addressTraceId?: string | null;
    googleRequest?: string | null;
    googleResponse?: string | null;
    googleGeocodeResponse?: string | null;
    googleApiDifferences?: string | null;
    googleGeocodeDestinationsResponse?: string | null;
    radarRequest?: string | null;
    radarResponse?: string | null;
    uberRequest?: string | null;
    uberResponse?: string | null;
    createdAt: string;
    updatedAt?: string | null;
}

export interface AddressGoogleInfo {
    addressId: number;
    addressTraceId?: string | null;
    googleRequest?: string | null;
    googleResponse?: string | null;
    googleGeocodeResponse?: string | null;
    googleApiDifferences?: string | null;
    googleGeocodeDestinationsResponse?: string | null;
}

export interface AddressRadarInfo {
    addressId: number;
    addressTraceId?: string | null;
    radarRequest?: string | null;
    radarResponse?: string | null;
}

export interface AddressUberInfo {
    addressId: number;
    addressTraceId?: string | null;
    uberRequest?: string | null;
    uberResponse?: string | null;
}

export interface AddressDetailsResponse {
    address: Address;
    addressValidationInfo: AddressValidationInfo;
}

export interface AddressReportResponse {
    address: Address;
    addressValidationInfo: AddressValidationInfo;
}

export interface UpdateAddressRequest {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    latitude?: number;
    longitude?: number;
}

export interface UpdateAddressResponse {
    traceId: string;
    address: Address;
    validationCompleted: boolean;
    cacheCleared: boolean;
    message: string;
}

export interface CorrectCoordinatesRequest {
    latitude: number;
    longitude: number;
    correctedBy: string;
    reason?: string;
}

export interface CorrectCoordinatesResponse {
    traceId: string;
    latitude: number;
    longitude: number;
    coordinateSource?: string | null;
    cacheCleared: boolean;
    message: string;
}

export interface CorrectFullAddressRequest {
    street1: string;
    street2?: string | null;
    city: string;
    state: string;
    zip: string;
    correctedBy: string;
    reason?: string;
}

export interface CorrectFullAddressResponse {
    previousTraceId: string;
    traceId?: string | null;
    addressId: number;
    /** True when the old record was invalid and got superseded + aliased; false when it was a valid address and stays live */
    supersededOldAddress: boolean;
    supersededAddressId?: number | null;
    reusedExistingAddress: boolean;
    aliasesCreated: number;
    aliasesRepointed: number;
    cacheCleared: boolean;
    address: Address;
    message: string;
}

export interface ValidateAddressRequest {
    street1: string;
    street2?: string | null;
    city: string;
    state: string;
    zip: string;
}

/** Request for POST api/v1/addresses/batch */
export interface BatchAddressRequest {
    traceIds?: string[];
    addresses?: ValidateAddressRequest[];
}

/** Response from POST api/v1/addresses/batch */
export interface BatchAddressResponse {
    statusByTraceId: (Record<string, unknown> | null)[];
    validations: Record<string, unknown>[];
}

export interface GeminiPreprocessingLogResponseDto {
    id: number;
    addressId?: number | null;
    traceId?: string | null;
    apiKeyId: number;
    originalStreet1: string;
    originalStreet2?: string | null;
    originalCity: string;
    originalState: string;
    originalZip: string;
    geminiCorrectedStreet1?: string | null;
    geminiCorrectedStreet2?: string | null;
    geminiCorrectedCity?: string | null;
    geminiCorrectedState?: string | null;
    geminiCorrectedZip?: string | null;
    geminiRequest?: string | null;
    geminiResponse?: string | null;
    modelUsed?: string | null;
    confidenceScore?: number | null;
    fieldConfidenceScores?: string | null;
    status: string;
    statusReason?: string | null;
    wasCorrectionAccepted: boolean;
    changeType?: string | null;
    changesDetailed?: string | null;
    processingTimeMs?: number | null;
    createdAt: string;
    updatedAt?: string | null;
}

const AddressManagementService = {
    getAddresses: async (params: AddressQueryParams): Promise<AddressResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;

        // Filter out undefined/null params
        const cleanParams: Record<string, any> = {};
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                cleanParams[key] = value;
            }
        });

        try {
            console.log('Fetching addresses with params:', cleanParams);
            const response = await axios.get(`${baseUrl}api/v1/address-management/addresses`, {
                params: cleanParams
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching addresses:", error);
            throw error;
        }
    },

    getAddressHistory: async (traceId: string): Promise<AddressHistoryRecord[]> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/history`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching history for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressNotes: async (traceId: string): Promise<AddressNote[]> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/notes`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching notes for traceId ${traceId}:`, error);
            throw error;
        }
    },

    addAddressNote: async (traceId: string, payload: CreateAddressNoteRequest): Promise<AddressNote> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.post(`${baseUrl}api/v1/address-management/details/${traceId}/notes`, payload);
            return response.data;
        } catch (error) {
            console.error(`Error adding note for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressProfile: async (traceId: string): Promise<Address> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const url = `${baseUrl}api/v1/address-management/details/${traceId}/profile`;
            console.log('Fetching address profile from URL:', url);
            const response = await axios.get(url);
            console.log('Address Profile Response:', response.data);
            // Extract the address object from the response; supersededByTraceId lives on the
            // wrapper, so merge it into the returned address
            return { ...response.data.address, supersededByTraceId: response.data.supersededByTraceId };
        } catch (error) {
            console.error(`Error fetching profile for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressGoogleInfo: async (traceId: string): Promise<AddressGoogleInfo> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/google`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Google info for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressRadarInfo: async (traceId: string): Promise<AddressRadarInfo> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/radar`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Radar info for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressUberInfo: async (traceId: string): Promise<AddressUberInfo> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/uber`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Uber info for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getFreshUberAddressDetails: async (traceId: string): Promise<any> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.post(`${baseUrl}api/v1/address-management/details/${traceId}/uber/fresh`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching fresh Uber address details for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getAddressReport: async (traceId: string): Promise<AddressReportResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/full`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Address Report for traceId ${traceId}:`, error);
            throw error;
        }
    },

    updateAddress: async (traceId: string, updates: UpdateAddressRequest): Promise<UpdateAddressResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.put(`${baseUrl}api/v1/address-management/addresses/${traceId}`, updates);
            return response.data;
        } catch (error) {
            console.error(`Error updating address ${traceId}:`, error);
            throw error;
        }
    },

    validateAddress: async (addressData: ValidateAddressRequest): Promise<any> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.post(`${baseUrl}api/v1/addresses/validate`, addressData);
            return response.data;
        } catch (error) {
            console.error("Error validating address:", error);
            throw error;
        }
    },

    processBatch: async (request: BatchAddressRequest): Promise<BatchAddressResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        const response = await axios.post<BatchAddressResponse>(`${baseUrl}api/v1/addresses/batch`, request);
        return response.data;
    },

    getGeminiLogs: async (traceId: string): Promise<GeminiPreprocessingLogResponseDto[]> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/gemini`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Gemini logs for traceId ${traceId}:`, error);
            throw error;
        }
    },

    getElasticLogs: async (traceId: string): Promise<any[]> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.get(`${baseUrl}api/v1/address-management/details/${traceId}/elastic`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching Elastic logs for traceId ${traceId}:`, error);
            throw error;
        }
    },

    correctCoordinates: async (traceId: string, payload: CorrectCoordinatesRequest): Promise<CorrectCoordinatesResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.put(`${baseUrl}api/v1/address-management/addresses/${traceId}/correct/coordinates`, payload);
            return response.data;
        } catch (error) {
            console.error(`Error correcting coordinates for ${traceId}:`, error);
            throw error;
        }
    },

    correctFullAddress: async (traceId: string, payload: CorrectFullAddressRequest): Promise<CorrectFullAddressResponse> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.put(`${baseUrl}api/v1/address-management/addresses/${traceId}/correct/full-address`, payload);
            return response.data;
        } catch (error) {
            console.error(`Error correcting address ${traceId}:`, error);
            throw error;
        }
    },

    refreshAddress: async (traceId: string): Promise<any> => {
        const baseUrl = config.API_URL?.endsWith('/') ? config.API_URL : `${config.API_URL}/`;
        try {
            const response = await axios.post(`${baseUrl}api/v1/address-management/addresses/${traceId}/refresh`);
            return response.data;
        } catch (error) {
            console.error(`Error refreshing address ${traceId}:`, error);
            throw error;
        }
    }
};

export default AddressManagementService;
